'use client';

import { ColumnDef } from '@tanstack/react-table';

import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge';
import { DisputeStatusBadge } from '@/components/disputes/dispute-status-badge';
import { humanizeReason } from '@/components/disputes/humanize-reason';
import { DateDisplay } from '@/components/date-display';
import { formatMoney } from '@/lib/money';
import { Dispute } from '@/lib/api/disputes';

interface GetDisputesTableColumnsProps {
  t: (key: string, v?: Record<any, any>) => string;
}

export function getDisputesTableColumns({ t }: GetDisputesTableColumnsProps): ColumnDef<Dispute>[] {
  return [
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.status')} />,
      cell: ({ row }) => <DisputeStatusBadge status={row.original.status} />,
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'reason',
      accessorKey: 'reason',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.reason')} />,
      cell: ({ row }) => <div className="text-sm">{humanizeReason(row.original.reason)}</div>,
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'amount',
      accessorKey: 'amountMinor',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.amount')} />,
      cell: ({ row }) => (
        <div className="text-sm font-semibold tabular-nums">
          {formatMoney(row.original.amountMinor, row.original.currency)}
        </div>
      ),
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'chargeId',
      accessorKey: 'chargeId',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.chargeId')} />,
      cell: ({ row }) => (
        <CopyableResourceBadge name={t('table.columns.chargeId')} value={row.original.chargeId} />
      ),
      enableSorting: false,
      enableHiding: true,
    },
    {
      id: 'openedAt',
      accessorKey: 'openedAt',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.openedAt')} />,
      cell: ({ row }) => (
        <DateDisplay value={row.original.openedAt} mode="split" className="text-sm tabular-nums leading-none" />
      ),
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'closedAt',
      accessorKey: 'closedAt',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.closedAt')} />,
      cell: ({ row }) => (
        <DateDisplay value={row.original.closedAt} mode="split" className="text-sm tabular-nums leading-none" />
      ),
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'evidenceDueBy',
      accessorKey: 'evidenceDueBy',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.evidenceDueBy')} />,
      cell: ({ row }) => (
        <DateDisplay value={row.original.evidenceDueBy} mode="split" className="text-sm tabular-nums leading-none" />
      ),
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'id',
      accessorKey: 'id',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.disputeId')} />,
      cell: ({ row }) => (
        <CopyableResourceBadge name={t('table.columns.disputeId')} value={row.original.id} />
      ),
      enableSorting: false,
      enableHiding: true,
    },
  ];
}
